import React, { useState, useCallback, useEffect, useContext, useRef, useMemo } from 'react';
import styles from './Feed.module.css';
import axios from 'axios';
import { useAuthUser } from '../../hooks/useAuthUser';
import { UserContext } from '../../contexts/UserContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { MdFavorite, MdFavoriteBorder, MdChatBubbleOutline, MdLocalFireDepartment, MdEmojiEvents, MdFitnessCenter, MdClose, MdDeleteOutline, MdPersonAdd } from 'react-icons/md';
import AppLoader from '../../components/AppLoader/AppLoader';
import formatDisplayName from '../../utils/formatDisplayName';
import VerifiedBadge from '../../components/VerifiedBadge/VerifiedBadge';

const POSTS_PER_PAGE = 40;
const CACHE_KEY = 'calcium_feed_cache';

const Feed = () => {
	const { user, isLoaded, isSignedIn } = useAuthUser();
	const { dbUser } = useContext(UserContext);
	const navigate = useNavigate();
	const location = useLocation();
	const API_URL = process.env.REACT_APP_API_URL;
	const activeApiBaseRef = useRef('');
	const latestFetchIdRef = useRef(0);
	const apiBases = useMemo(() => {
		const trimmedBase = (API_URL || '').trim().replace(/\/+$/, '');
		if (!trimmedBase) return [];
		const candidates = [trimmedBase];
		if (!/\/api$/i.test(trimmedBase)) {
			candidates.push(`${trimmedBase}/api`);
		} else {
			candidates.push(trimmedBase.replace(/\/api$/i, ''));
		}
		return [...new Set(candidates)];
	}, [API_URL]);

	const [posts, setPosts] = useState(() => {
		try {
			const cached = sessionStorage.getItem(CACHE_KEY);
			if (cached) return JSON.parse(cached);
		} catch { /* ignore */ }
		return [];
	});
	const [loading, setLoading] = useState(true);
	const [feedError, setFeedError] = useState('');
	const [showComposer, setShowComposer] = useState(false);
	const [newPostContent, setNewPostContent] = useState('');
	const [posting, setPosting] = useState(false);
	const [deletingPostID, setDeletingPostID] = useState('');
	const [postTags, setPostTags] = useState([]);
	const [tagMode, setTagMode] = useState(null);
	const [tagSearch, setTagSearch] = useState('');
	const [tagResults, setTagResults] = useState([]);
	const [tagSearching, setTagSearching] = useState(false);
	const [page, setPage] = useState(1);
	const [hasMore, setHasMore] = useState(true);
	const [loadingMore, setLoadingMore] = useState(false);

	const requestWithFallback = useCallback(async (requestFactory, _requestLabel = 'feed-request') => {
		const orderedBases = activeApiBaseRef.current && apiBases.includes(activeApiBaseRef.current)
			? [activeApiBaseRef.current, ...apiBases.filter(base => base !== activeApiBaseRef.current)]
			: apiBases;
		let lastError = null;
		for (const base of orderedBases) {
			try {
				const response = await requestFactory(base);
				activeApiBaseRef.current = base;
				return response;
			} catch (err) {
				const status = err?.response?.status;
				lastError = err;
				if (status !== 404) break;
			}
		}
		if (lastError) throw lastError;
		throw new Error('Missing API base URL');
	}, [apiBases]);

	const fetchFeed = useCallback(async (pageNum = 1, append = false) => {
		if (!isLoaded) return;
		const fetchId = ++latestFetchIdRef.current;
		if (!append) setFeedError('');
		if (append) setLoadingMore(true);

		try {
			const res = await requestWithFallback((base) => {
				const endpoint = isSignedIn && user?.id
					? `${base}/feed/timeline/${user.id}?page=${pageNum}&limit=${POSTS_PER_PAGE}`
					: `${base}/feed/public?page=${pageNum}&limit=${POSTS_PER_PAGE}`;
				return axios.get(endpoint);
			}, 'fetch-feed');

			if (fetchId === latestFetchIdRef.current) {
				const newPosts = res.data.posts || [];
				const pagination = res.data.pagination;

				if (append) {
					setPosts(prev => {
						const existingIds = new Set(prev.map(p => p._id));
						const unique = newPosts.filter(p => !existingIds.has(p._id));
						const merged = [...prev, ...unique];
						try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(merged)); } catch { /* ignore */ }
						return merged;
					});
				} else {
					setPosts(newPosts);
					try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(newPosts)); } catch { /* ignore */ }
				}

				setHasMore(pagination ? pageNum < pagination.pages : newPosts.length === POSTS_PER_PAGE);
			}
		} catch (err) {
			console.error('Feed fetch error:', err.message);
			if (fetchId === latestFetchIdRef.current && !append) {
				setFeedError('We could not refresh your feed right now.');
			}
		} finally {
			if (fetchId === latestFetchIdRef.current) {
				setLoading(false);
				setLoadingMore(false);
			}
		}
	}, [isLoaded, isSignedIn, requestWithFallback, user?.id]);

	useEffect(() => {
		if (!isLoaded) return;
		setLoading(posts.length === 0);
		setPage(1);
		fetchFeed(1);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoaded, isSignedIn, user?.id]);

	useEffect(() => {
		const handleVisibilityChange = () => {
			if (document.visibilityState === 'visible' && isLoaded) {
				fetchFeed(1);
			}
		};
		document.addEventListener('visibilitychange', handleVisibilityChange);
		return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
	}, [fetchFeed, isLoaded]);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		if (params.get('compose') !== '1') return;

		setShowComposer(true);
		params.delete('compose');
		const nextSearch = params.toString();
		navigate({
			pathname: location.pathname,
			search: nextSearch ? `?${nextSearch}` : ''
		}, { replace: true });
	}, [location.pathname, location.search, navigate]);

	const loadMore = () => {
		const nextPage = page + 1;
		setPage(nextPage);
		fetchFeed(nextPage, true);
	};

	const searchTagTargets = useCallback(async (term, mode) => {
		if (!term.trim()) { setTagResults([]); return; }
		setTagSearching(true);
		try {
			const endpoint = mode === 'user' ? 'search/people' : 'search/routines';
			const res = await requestWithFallback((base) => axios.get(`${base}/${endpoint}/${encodeURIComponent(term)}`), 'tag-search');
			if (mode === 'user') {
				setTagResults((res.data || []).slice(0, 8).map(u => ({ tagType: 'user', refID: u.userID, name: u.username, profileImageLink: u.profileImageLink })));
			} else {
				const routines = Array.isArray(res.data) ? res.data : [];
				setTagResults(routines.slice(0, 8).map(r => ({ tagType: 'routine', refID: r.userID, name: r.routine?.routineName || r.routineName, creatorUsername: r.username })));
			}
		} catch { setTagResults([]); }
		finally { setTagSearching(false); }
	}, [requestWithFallback]);

	useEffect(() => {
		if (!tagMode || !tagSearch.trim()) { setTagResults([]); return; }
		const timer = setTimeout(() => searchTagTargets(tagSearch, tagMode), 300);
		return () => clearTimeout(timer);
	}, [tagSearch, tagMode, searchTagTargets]);

	const addTag = (tag) => {
		if (postTags.some(t => t.tagType === tag.tagType && t.refID === tag.refID && t.name === tag.name)) return;
		setPostTags(prev => [...prev, { tagType: tag.tagType, refID: tag.refID, name: tag.name }]);
		setTagMode(null);
		setTagSearch('');
		setTagResults([]);
	};

	const removeTag = (idx) => setPostTags(prev => prev.filter((_, i) => i !== idx));

	const createPost = async () => {
		if (!newPostContent.trim() || !user?.id || posting) return;
		setPosting(true);
		try {
			await requestWithFallback((base) => {
				return axios.post(`${base}/feed`, {
					userID: user.id,
					username: dbUser?.username || user.firstName,
					profileImageLink: user.profileImageUrl,
					content: newPostContent.trim(),
					tags: postTags,
					type: 'post',
					visibility: 'public'
				});
			}, 'create-post');
			setNewPostContent('');
			setPostTags([]);
			setShowComposer(false);
			setPage(1);
			fetchFeed(1);
		} catch (err) {
			console.error('Post error:', err.message);
		} finally {
			setPosting(false);
		}
	};

	const toggleLike = async (postID) => {
		if (!user?.id) return;
		setPosts(prev => prev.map(p => {
			if (p._id !== postID) return p;
			const alreadyLiked = p.likes?.some(l => l.userID === user.id);
			return {
				...p,
				likes: alreadyLiked
					? (p.likes || []).filter(l => l.userID !== user.id)
					: [...(p.likes || []), { userID: user.id, username: dbUser?.username || user.firstName }]
			};
		}));
		try {
			const res = await requestWithFallback((base) => {
				return axios.post(`${base}/feed/${postID}/like`, {
					userID: user.id,
					username: dbUser?.username || user.firstName
				});
			}, 'toggle-like');
			setPosts(prev => prev.map(p => p._id === postID ? res.data : p));
		} catch (err) {
			console.error('Like error:', err.message);
			fetchFeed(1);
		}
	};

	const deletePost = async (postID) => {
		if (!user?.id || !postID || deletingPostID) return;
		setDeletingPostID(postID);
		try {
			await requestWithFallback((base) => {
				return axios.delete(`${base}/feed/${postID}`, {
					data: { userID: user.id }
				});
			}, 'delete-post');
			setPosts((prev) => prev.filter((post) => post._id !== postID));
		} catch (err) {
			console.error('Delete post error:', err.message);
		} finally {
			setDeletingPostID('');
		}
	};

	const getPostIcon = (type) => {
		switch (type) {
			case 'workout_complete': return <MdFitnessCenter />;
			case 'streak': return <MdLocalFireDepartment />;
			case 'milestone': return <MdEmojiEvents />;
			default: return null;
		}
	};

	const getTimeAgo = (dateStr) => {
		const diff = Date.now() - new Date(dateStr).getTime();
		const mins = Math.floor(diff / 60000);
		if (mins < 1) return 'now';
		if (mins < 60) return `${mins}m`;
		const hrs = Math.floor(mins / 60);
		if (hrs < 24) return `${hrs}h`;
		const days = Math.floor(hrs / 24);
		if (days < 7) return `${days}d`;
		return `${Math.floor(days / 7)}w`;
	};

	if (!isLoaded || (loading && posts.length === 0)) {
		return <AppLoader message='Loading feed...' />;
	}

	return (
		<div className={styles.FeedPage}>
			<div className={styles.FeedHeader}>
				<h1 className={styles.FeedTitle}>Feed</h1>
			</div>

			{user && (
				<div className={styles.ComposerTrigger}>
					{!showComposer ? (
						<button className={styles.ComposerTriggerBtn} onClick={() => setShowComposer(true)}>
							<img
								src={user.profileImageUrl}
								alt=""
								className={styles.ComposerAvatar}
							/>
							<span className={styles.ComposerPlaceholder}>What's on your mind?</span>
						</button>
					) : (
						<div className={styles.ComposerExpanded}>
							<div className={styles.ComposerHeader}>
								<img src={user.profileImageUrl} alt="" className={styles.ComposerAvatar} />
								<span className={styles.ComposerUsername}>
									{formatDisplayName(dbUser?.username || user.firstName)}
									<VerifiedBadge userPlan={dbUser?.userPlan} totalXP={dbUser?.totalXP} />
								</span>
								<button className={styles.ComposerClose} onClick={() => setShowComposer(false)}>
									<MdClose />
								</button>
							</div>
							<textarea
								className={styles.ComposerTextarea}
								value={newPostContent}
								onChange={(e) => setNewPostContent(e.target.value)}
								placeholder="Share your workout, progress, or motivate the community..."
								rows={4}
								autoFocus
							/>

							{postTags.length > 0 && (
								<div className={styles.TagList}>
									{postTags.map((tag, i) => (
										<span key={i} className={styles.TagChip}>
											{tag.tagType === 'user' ? '@' : '#'}{tag.name}
											<button type='button' className={styles.TagRemove} onClick={() => removeTag(i)}><MdClose /></button>
										</span>
									))}
								</div>
							)}

							{tagMode && (
								<div className={styles.TagSearchBox}>
									<input
										className={styles.TagSearchInput}
										placeholder={tagMode === 'user' ? 'Search people to tag...' : 'Search routines to tag...'}
										value={tagSearch}
										onChange={(e) => setTagSearch(e.target.value)}
										autoFocus
									/>
									{tagSearching && <span className={styles.TagSearchHint}>Searching...</span>}
									{tagResults.length > 0 && (
										<div className={styles.TagDropdown}>
											{tagResults.map((item, i) => (
												<button
													key={i}
													type='button'
													className={styles.TagOption}
													onClick={() => addTag(item)}
												>
													{item.tagType === 'user' ? (
														<>
															{item.profileImageLink && <img src={item.profileImageLink} alt='' className={styles.TagOptionAvatar} />}
															<span>@{formatDisplayName(item.name)}</span>
														</>
													) : (
														<>
															<MdFitnessCenter />
															<span>{item.name}</span>
															{item.creatorUsername && <span className={styles.TagOptionMeta}>by {formatDisplayName(item.creatorUsername)}</span>}
														</>
													)}
												</button>
											))}
										</div>
									)}
								</div>
							)}

							<div className={styles.ComposerActions}>
								<div className={styles.ComposerTagActions}>
									<button
										type='button'
										className={`${styles.TagBtn} ${tagMode === 'user' ? styles.TagBtnActive : ''}`}
										onClick={() => { setTagMode(tagMode === 'user' ? null : 'user'); setTagSearch(''); setTagResults([]); }}
									>
										<MdPersonAdd />
										<span>Tag User</span>
									</button>
									<button
										type='button'
										className={`${styles.TagBtn} ${tagMode === 'routine' ? styles.TagBtnActive : ''}`}
										onClick={() => { setTagMode(tagMode === 'routine' ? null : 'routine'); setTagSearch(''); setTagResults([]); }}
									>
										<MdFitnessCenter />
										<span>Tag Routine</span>
									</button>
								</div>
								<button
									className={styles.PostBtn}
									onClick={createPost}
									disabled={!newPostContent.trim() || posting}
								>
									{posting ? 'Posting...' : 'Post'}
								</button>
							</div>
						</div>
					)}
				</div>
			)}

			<div className={styles.PostsList}>
				{feedError && posts.length === 0 ? (
					<div className={styles.EmptyFeed}>
						<MdChatBubbleOutline className={styles.EmptyIcon} />
						<h3>Feed unavailable</h3>
						<p>{feedError}</p>
						<button className={styles.RetryBtn} onClick={() => fetchFeed(1)}>Retry</button>
					</div>
				) : null}
				{!feedError && posts.length === 0 ? (
					<div className={styles.EmptyFeed}>
						<MdFitnessCenter className={styles.EmptyIcon} />
						<h3>No posts yet</h3>
						<p>Follow people or create a post to get your feed going!</p>
					</div>
				) : (
					<>
						{posts.map(post => {
							const isLiked = post.likes?.some(l => l.userID === user?.id);
							const isOwnPost = post.userID === user?.id;

							return (
								<div
									key={post._id}
									className={`${styles.PostCard} ${styles.PostCardClickable}`}
									onClick={() => navigate(`/feed/post/${post._id}`)}
									role='button'
									tabIndex={0}
									onKeyDown={(event) => {
										if (event.key === 'Enter' || event.key === ' ') {
											event.preventDefault();
											navigate(`/feed/post/${post._id}`);
										}
									}}
								>
									<div className={styles.PostHeader}>
										<Link
											to={`/profile/${post.userID}`}
											className={styles.PostAuthor}
											onClick={(event) => event.stopPropagation()}
										>
											{post.profileImageLink ? (
												<img src={post.profileImageLink} alt="" className={styles.PostAvatar} />
											) : (
												<div className={styles.PostAvatarPlaceholder}>{post.username?.[0]?.toUpperCase()}</div>
											)}
											<div className={styles.PostAuthorInfo}>
												<span className={styles.PostUsername}>
													{formatDisplayName(post.username)}
													<VerifiedBadge userPlan={post.userPlan} totalXP={post.totalXP} size='small' />
												</span>
												<span className={styles.PostTime}>{getTimeAgo(post.createdAt)}</span>
											</div>
										</Link>
										{post.type !== 'post' && (
											<span className={`${styles.PostTypeBadge} ${styles[`PostType_${post.type}`]}`}>
												{getPostIcon(post.type)}
												{post.type === 'workout_complete' && 'Workout'}
												{post.type === 'streak' && `${post.streakCount}d Streak`}
												{post.type === 'milestone' && 'Milestone'}
											</span>
										)}
										{isOwnPost ? (
											<button
												type='button'
												className={styles.PostDeleteBtn}
												onClick={(event) => {
													event.stopPropagation();
													deletePost(post._id);
												}}
												disabled={deletingPostID === post._id}
											>
												<MdDeleteOutline />
												<span>{deletingPostID === post._id ? 'Deleting...' : 'Delete'}</span>
											</button>
										) : null}
									</div>

									<p className={styles.PostContent}>{post.content}</p>

									{post.routineRef?.routineName && (
										<Link
											to={`/stryd/${post.routineRef.creatorID}/${post.routineRef.routineName}`}
											className={styles.RoutineRefCard}
											onClick={(event) => event.stopPropagation()}
										>
											<MdFitnessCenter />
											<span>{post.routineRef.routineName}</span>
											{post.xpEarned > 0 && <span className={styles.XPBadge}>+{post.xpEarned} XP</span>}
										</Link>
									)}

									{post.tags?.length > 0 && (
										<div className={styles.PostTags}>
											{post.tags.map((tag, i) => (
												<Link
													key={i}
													className={styles.Tag}
													to={tag.tagType === 'user' ? `/profile/${tag.refID}` : `/stryd/${tag.refID}/${tag.name}`}
													onClick={(event) => event.stopPropagation()}
												>
													{tag.tagType === 'user' ? '@' : '#'}{tag.name}
												</Link>
											))}
										</div>
									)}

									<div className={styles.PostActions}>
										<button
											className={`${styles.ActionBtn} ${isLiked ? styles.Liked : ''}`}
											onClick={(event) => {
												event.stopPropagation();
												toggleLike(post._id);
											}}
										>
											{isLiked ? <MdFavorite /> : <MdFavoriteBorder />}
											<span>{post.likes?.length || 0} {(post.likes?.length || 0) === 1 ? 'like' : 'likes'}</span>
										</button>
										<div className={styles.ActionBtn}>
											<MdChatBubbleOutline />
											<span>{post.comments?.length || 0} {(post.comments?.length || 0) === 1 ? 'reply' : 'replies'}</span>
										</div>
									</div>
								</div>
							);
						})}

						{hasMore && (
							<div className={styles.LoadMoreWrapper}>
								<button
									className={styles.LoadMoreBtn}
									onClick={loadMore}
									disabled={loadingMore}
								>
									{loadingMore ? 'Loading...' : 'Load more'}
								</button>
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
};

export default Feed;
